import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Editor dos modelos de Mala Direta (Jodit, MIT), no estilo do Word: fonte, tamanho em pt, espaçamento entre linhas,
// cores, alinhamento, listas, tabela (mesclar/dividir células, bordas), imagem (redimensiona com o mouse), link, símbolos,
// localizar, colar do Word e o HTML. Carregado só quando a tela abre (não pesa no resto do sistema).
// A área de edição tem a fonte, o tamanho e a cor do e-mail (MalaDiretaService.montar: Arial 14px #333) e fundo branco,
// para o texto aparecer como vai no e-mail. Tudo sai com estilo no próprio elemento (o e-mail não tem CSS).

// Fontes que existem em qualquer computador e celular (a que só existe na máquina de quem escreve aparece trocada)
const FONTES = {
  "": "Padrão (Arial)",
  "Arial, Helvetica, sans-serif": "Arial",
  "Calibri, Arial, sans-serif": "Calibri",
  "Georgia, 'Times New Roman', serif": "Georgia",
  "Tahoma, Verdana, sans-serif": "Tahoma",
  "'Times New Roman', Times, serif": "Times New Roman",
  "'Trebuchet MS', Arial, sans-serif": "Trebuchet MS",
  "Verdana, Geneva, sans-serif": "Verdana",
  "'Courier New', Courier, monospace": "Courier New",
};

const TAMANHOS = [8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48];

const BOTOES = [
  "undo", "redo", "|",
  "font", "fontsize", "paragraph", "lineHeight", "|",
  "bold", "italic", "underline", "strikethrough", "superscript", "subscript", "|",
  "brush", "eraser", "copyformat", "|",
  "left", "center", "right", "justify", "|",
  "ul", "ol", "outdent", "indent", "|",
  "table", "image", "link", "hr", "symbols", "|",
  "find", "selectall", "|",
  "source", "fullsize", "preview", "print",
];

// Como o e-mail: a mesma fonte do MalaDiretaService.montar e o mesmo parágrafo (MalaDiretaService.paragrafosDoEmail:
// sem margem/entrelinha própria, 0 em cima, 5px embaixo e 1.4) e fundo branco também no tema escuro. Célula sem borda
// própria aparece tracejada só aqui (guia, como no Word).
const ESTILO_EMAIL =
  "body{font-family:Arial,sans-serif;font-size:14px;color:#333;line-height:normal;background:#fff;padding:16px 20px;}" +
  "p{margin:0 0 5px;line-height:1.4;}" +
  "td,th{border:1px dashed #d0d0d0;}";

function criarConfig(Jodit, darkMode) {
  return {
    language: "pt_br",
    theme: darkMode ? "dark" : "default",
    height: 650,
    minHeight: 400,
    iframe: true,
    iframeStyle: (Jodit.defaultOptions.iframeStyle || "") + ESTILO_EMAIL,
    toolbarAdaptive: false,
    toolbarSticky: false,
    buttons: BOTOES,
    defaultFontSizePoints: "pt",
    controls: {
      font: { list: Jodit.atom(FONTES) },
      fontsize: { list: Jodit.atom(TAMANHOS) },
    },
    // Tabela nova: com grade (como a "Tabela com grade" do Word); a borda vai na célula, que é o que o e-mail mostra
    createAttributes: {
      table: { style: "border-collapse: collapse; width: 100%;" },
      td: { style: "border: 1px solid #000000; padding: 4px; vertical-align: top;" },
      th: { style: "border: 1px solid #000000; padding: 4px; vertical-align: top;" },
    },
    // Imagem enviada ou colada fica no próprio modelo (data:image); no envio vira anexo embutido (cid)
    uploader: { insertImageAsBase64URI: true },
    disablePlugins: ["ai-assistant", "video", "file", "poweredByJodit"],
    hidePoweredByJodit: true,
    showXPathInStatusbar: false,
    beautifyHTML: false,
    placeholder: "Escreva a mensagem ou importe o .docx...",
  };
}

/**
 * Modelo gravado pelo editor antigo (Quill): as classes ql-* viram estilo no elemento, como o backend faz no envio
 * (MalaDiretaService.converterClassesDoEditor), juntando com o estilo que o elemento já tinha.
 */
export function htmlDoEditorAntigo(html) {
  if (!html || html.indexOf("ql-") < 0) return html || "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const estilos = {
    "ql-align-center": "text-align: center;",
    "ql-align-right": "text-align: right;",
    "ql-align-justify": "text-align: justify;",
    "ql-size-small": "font-size: 0.75em;",
    "ql-size-large": "font-size: 1.5em;",
    "ql-size-huge": "font-size: 2.5em;",
    "ql-font-serif": "font-family: Georgia, 'Times New Roman', serif;",
    "ql-font-monospace": "font-family: 'Courier New', Courier, monospace;",
  };
  doc.body.querySelectorAll("[class]").forEach((el) => {
    const novos = [];
    Array.from(el.classList).forEach((c) => {
      const recuo = /^ql-indent-(\d+)$/.exec(c);
      if (estilos[c]) novos.push(estilos[c]);
      else if (recuo) novos.push(`padding-left: ${Number(recuo[1]) * 3}em;`);
      if (c.startsWith("ql-")) el.classList.remove(c);
    });
    if (novos.length) el.setAttribute("style", `${el.getAttribute("style") || ""} ${novos.join(" ")}`.trim());
    if (!el.classList.length) el.removeAttribute("class");
  });
  return doc.body.innerHTML;
}

/**
 * Põe o texto (ex.: a variável) no lugar da seleção do editor, com a formatação de onde ela está; sem seleção no texto,
 * no fim. A seleção continua valendo depois de clicar fora (o editor fica num iframe, com a sua própria seleção).
 */
export function inserirTexto(editor, texto) {
  if (!editor) return;
  const sel = editor.s.sel;
  let range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
  if (!range || !editor.editor.contains(range.startContainer)) {
    editor.s.setCursorIn(editor.editor, false);
    range = editor.s.range;
  }
  range.deleteContents();
  const no = editor.ed.createTextNode(texto);
  range.insertNode(no);
  editor.s.setCursorAfter(no);
  editor.synchronizeValues();
}

function EditorModelo({ valor = "", darkMode = false, editorRef, aoFocar = null }) {
  const [modulo, setModulo] = useState(null);
  const [erro, setErro] = useState(null);
  // O jodit-react recria o editor quando a função de registro muda: ela é fixa e chama sempre o aoFocar mais recente
  const aoFocarRef = useRef(aoFocar);
  aoFocarRef.current = aoFocar;
  const registrar = useCallback(
    (jodit) => {
      // eslint-disable-next-line no-param-reassign
      editorRef.current = jodit;
      if (jodit) jodit.events.on("focus", () => aoFocarRef.current && aoFocarRef.current());
    },
    [editorRef]
  );

  useEffect(() => {
    let vivo = true;
    import("jodit-react")
      .then((m) => vivo && setModulo(m))
      .catch((e) => vivo && setErro(e?.message || "Não foi possível carregar o editor."));
    return () => {
      vivo = false;
    };
  }, []);

  const config = useMemo(() => (modulo ? criarConfig(modulo.Jodit, darkMode) : null), [modulo, darkMode]);

  if (erro) {
    return <MDTypography variant="caption" color="error">{erro}</MDTypography>;
  }
  if (!modulo) {
    return (
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} py={6}>
        <CircularProgress size={20} color="info" />
        <MDTypography variant="caption" color="text">Carregando o editor...</MDTypography>
      </MDBox>
    );
  }
  const JoditEditor = modulo.default;
  return <JoditEditor editorRef={registrar} value={valor} config={config} />;
}

EditorModelo.propTypes = {
  valor: PropTypes.string,
  darkMode: PropTypes.bool,
  editorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  aoFocar: PropTypes.func,
};

export default EditorModelo;
